import {
    FETCH_START,
    FETCH_SUCCESS,
    FETCH_ERROR,
    SET_PAGE,
    SET_SEARCH,
    SHOW_MESSAGE,
    GET_EXCLUSIVE_PROPERTY,
    SET_EXCLUSIVE_PROPERTY_SUB_TYPE,
} from '../../shared/constants/ActionTypes';
import jwtAxios from '@crema/services/auth/jwt-auth/jwt-api';

const getErrorMessage = (error) => {
    const data = error?.response?.data;
    if (data?.errors && typeof data.errors === 'object') {
        const messages = Object.entries(data.errors).flatMap(([, msgs]) =>
            Array.isArray(msgs) ? msgs : [msgs],
        );
        if (messages.length) {
            return messages.join(' ');
        }
    }
    return data?.message || data?.msg || error?.message;
};

const normalizeExclusivePropertyList = (responseData) => {
    const payload = responseData?.data ?? responseData;
    if (Array.isArray(payload)) {
        return { data: payload, total: payload.length };
    }
    const data =
        payload?.data ??
        payload?.value ??
        payload?.items ??
        payload?.results ??
        payload?.properties ??
        [];
    const total =
        payload?.total ??
        payload?.totalCount ??
        payload?.count ??
        (Array.isArray(data) ? data.length : 0);
    return {
        data: Array.isArray(data) ? data : [],
        total,
    };
};

export const getExclusivePropertyById = (id) => {
    return () =>
        jwtAxios.get(`/exclusiveproperty/${id}`).then((res) => {
            if (res.status === 200) {
                const payload = res.data?.data ?? res.data;
                if (payload?.succeeded === false) {
                    return Promise.reject(
                        new Error(
                            payload?.message ||
                                'Failed to load exclusive property details.',
                        ),
                    );
                }
                return payload;
            }
            return Promise.reject(
                new Error('Failed to load exclusive property details.'),
            );
        });
};

export const getExclusiveProperty = (limit = 10) => {
    return (dispatch, getState) => {
        const state = getState().exclusiveproperty;
        dispatch({ type: FETCH_START });
        // Admin list: GET /exclusiveproperty/list
        // Query: pageNo, pageSize, keyword, propertySubType
        jwtAxios
            .get('/exclusiveproperty/list', {
                params: {
                    pageNo: state.page,
                    pageSize: limit,
                    keyword: state.search || '',
                    propertySubType: state.propertySubType || '',
                },
            })
            .then((data) => {
                if (data.status === 200) {
                    dispatch({ type: FETCH_SUCCESS });
                    dispatch({
                        type: GET_EXCLUSIVE_PROPERTY,
                        payload: normalizeExclusivePropertyList(data.data),
                    });
                } else {
                    dispatch({
                        type: FETCH_ERROR,
                        payload: 'Something went wrong, Please try again!',
                    });
                }
            })
            .catch((error) => {
                dispatch({
                    type: FETCH_ERROR,
                    payload: getErrorMessage(error),
                });
            });
    };
};

export const addExclusiveProperty = (formData) => {
    return (dispatch) => {
        dispatch({ type: FETCH_START });
        jwtAxios
            .post('/exclusiveproperty', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            .then((data) => {
                if (data.status === 200) {
                    if (data.data?.succeeded === false) {
                        dispatch({
                            type: FETCH_ERROR,
                            payload:
                                data.data?.message ||
                                'Something went wrong, Please try again!',
                        });
                        return;
                    }
                    dispatch({ type: FETCH_SUCCESS });
                    dispatch({ type: SHOW_MESSAGE, payload: data?.data?.message });
                    dispatch(getExclusiveProperty());
                } else {
                    dispatch({
                        type: FETCH_ERROR,
                        payload: 'Something went wrong, Please try again!',
                    });
                }
            })
            .catch((error) => {
                dispatch({
                    type: FETCH_ERROR,
                    payload: getErrorMessage(error),
                });
            });
    };
};

export const editExclusiveProperty = (formData) => {
    return (dispatch) => {
        dispatch({ type: FETCH_START });
        jwtAxios
            .post(`/exclusiveproperty`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            .then((data) => {
                if (data.status === 200) {
                    if (data.data?.succeeded === false) {
                        dispatch({
                            type: FETCH_ERROR,
                            payload:
                                data.data?.message ||
                                'Something went wrong, Please try again!',
                        });
                        return;
                    }
                    dispatch({ type: FETCH_SUCCESS });
                    dispatch(getExclusiveProperty());
                    dispatch({
                        type: SHOW_MESSAGE,
                        payload: data?.data?.message,
                    });
                } else {
                    dispatch({
                        type: FETCH_ERROR,
                        payload: 'Something went wrong, Please try again!',
                    });
                }
            })
            .catch((error) => {
                dispatch({
                    type: FETCH_ERROR,
                    payload: getErrorMessage(error),
                });
            });
    };
};

export const deleteExclusiveProperty = (id) => {
    return (dispatch) => {
        dispatch({ type: FETCH_START });
        jwtAxios
            .delete(`/exclusiveproperty/${encodeURIComponent(id)}`)
            .then((data) => {
                if (data.status === 200) {
                    dispatch({ type: FETCH_SUCCESS });
                    dispatch({
                        type: SHOW_MESSAGE,
                        payload: data?.data?.message,
                    });
                    dispatch(getExclusiveProperty());
                } else {
                    dispatch({
                        type: FETCH_ERROR,
                        payload: 'Something went wrong, Please try again!',
                    });
                }
            })
            .catch((error) => {
                dispatch({
                    type: FETCH_ERROR,
                    payload: getErrorMessage(error),
                });
            });
    };
};

export const deleteImages = (formData) => {
    return (dispatch) => {
        dispatch({ type: FETCH_START });
        jwtAxios
            .patch(`/removeimage`, {}, {
                params: {
                    key: formData.key
                }
            })
            .then((data) => {
                if (data.status === 200) {
                    dispatch({ type: FETCH_SUCCESS });
                    dispatch({
                        type: SHOW_MESSAGE,
                        payload: data?.data?.message,
                    });
                } else {
                    dispatch({
                        type: FETCH_ERROR,
                        payload: 'Something went wrong, Please try again!',
                    });
                }
            })
            .catch((error) => {
                dispatch({
                    type: FETCH_ERROR,
                    payload: getErrorMessage(error),
                });
            });
    };
};

export const verifyExclusiveProperty = (data) => {
    return (dispatch) => {
        dispatch({ type: FETCH_START });
        jwtAxios
            .patch(`/verifyexclusiveproperty`, {}, {
                params: {
                    IsVerify: data.IsVerify,
                    Id: data.id,
                }
            })
            .then((data) => {
                if (data.status === 200) {
                    dispatch({ type: FETCH_SUCCESS });
                    dispatch({
                        type: SHOW_MESSAGE,
                        payload: data?.data?.message,
                    });
                    dispatch(getExclusiveProperty());
                } else {
                    dispatch({
                        type: FETCH_ERROR,
                        payload: 'Something went wrong, Please try again!',
                    });
                }
            })
            .catch((error) => {
                dispatch({
                    type: FETCH_ERROR,
                    payload: getErrorMessage(error),
                });
            });
    };
};

export const setPage = (page) => {
    return (dispatch) => {
        dispatch({ type: SET_PAGE, payload: page });
    };
};

export const setSearch = (search) => {
    return (dispatch) => {
        dispatch({ type: SET_SEARCH, payload: search });
    };
};

export const setPropertySubType = (propertySubType) => {
    return (dispatch) => {
        dispatch({ type: SET_EXCLUSIVE_PROPERTY_SUB_TYPE, payload: propertySubType || '' });
    };
};
